import { useSearchParams } from 'react-router-dom'
import AdyenCheckout from '@adyen/adyen-web'
import config from '../../config'
import React, { useCallback, useEffect, useState } from 'react'
import { Banner } from '@ordr-as/umami-react'
import Container from '../../components/layout/Container'
import '@adyen/adyen-web/dist/adyen.css'
import { ToggleSwitch } from '../../components/Toggle'
import { authorisePayment, cancelPayment, cancelPaymentWithBeacon } from '../../api/paymentApi'
import {
    useLogCardCheckoutCancelPayment,
    useLogCardCheckoutClickBackButton,
    useLogCardCheckoutLoadPage,
    useLogCardCheckoutAuthorisePayment,
    useLogCardCheckoutRedirectToReturnUrl,
    useLogCardCheckoutRedirectToAction,
} from '../../utils/analytics'
import './style/card.css'
import { AuthorisePaymentRequest } from '../../../generated-api'
const testLocations = [
    '86900823', // This is the PROD location called "[DEMO] Integrated Terminals Pilot"
]

const CardCheckout = () => {
    const [searchParams] = useSearchParams()
    const [hasSubmitted, setHasSubmitted] = useState(false)
    const sessionId = searchParams.get('sessionId')
    const sessionData = searchParams.get('sessionData')
    const paymentReference = searchParams.get('paymentReference')!
    const executingLocationId = searchParams.get('executingLocationId')!
    const hasRequiredParams = sessionId && sessionData && paymentReference
    const [showTestOptions, setShowTestOptions] = useState(false)
    const [force3DSecure, setForce3DSecure] = useState(false)

    // Analytics
    const logLoadPage = useLogCardCheckoutLoadPage()
    const logClickBackButton = useLogCardCheckoutClickBackButton()
    const logCancelPayment = useLogCardCheckoutCancelPayment()
    const logAuthorisePayment = useLogCardCheckoutAuthorisePayment()
    const logRedirectToReturnUrl = useLogCardCheckoutRedirectToReturnUrl()
    const logRedirectToAction = useLogCardCheckoutRedirectToAction()

    useEffect(() => {
        logLoadPage(paymentReference)

        addEventListeners()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onBackButtonClicked = async (): Promise<boolean> => {
        if (hasSubmitted) {
            return new Promise(() => false)
        }

        logClickBackButton(paymentReference)

        // We need to unregister the event listener to prevent cancelPayment to be
        // called again once the back button has done the navigation.
        window.removeEventListener('unload', onUnload)
        window.removeEventListener('beforeunload', onUnload)

        logCancelPayment(paymentReference, 'fetch')

        return cancelPayment(paymentReference!).then(() => true)
    }

    const onUnload = useCallback(() => {
        window.removeEventListener('unload', onUnload)
        window.removeEventListener('beforeunload', onUnload)

        logCancelPayment(paymentReference, 'beacon')

        cancelPaymentWithBeacon(paymentReference)
    }, [paymentReference, logCancelPayment])

    useEffect(() => {
        if (hasRequiredParams) {
            console.log('Initializing Checkout')

            const checkoutConfig = createCheckoutConfig(sessionId!, sessionData!, force3DSecure)

            AdyenCheckout(checkoutConfig).then((checkout) => {
                checkout.create('card').mount('#card-checkout-container')
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTestOptions, force3DSecure])

    const addEventListeners = () => {
        window.addEventListener('unload', onUnload)
        window.addEventListener('beforeunload', onUnload)

        return () => {
            window.removeEventListener('unload', onUnload)
            window.removeEventListener('beforeunload', onUnload)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSubmitPayment = async (state: any, component: any, force3DSecure: boolean) => {
        component.setStatus('loading')

        if (!state.isValid) {
            alert('Invalid card details. Please make sure you have entered the correct information.')
            return
        }

        setHasSubmitted(true)

        const request = {
            browserInfo: {
                userAgent: state.data.browserInfo.userAgent,
                colorDepth: state.data.browserInfo.colorDepth,
                javaEnabled: state.data.browserInfo.javaEnabled,
                language: state.data.browserInfo.language,
                screenHeight: state.data.browserInfo.screenHeight,
                screenWidth: state.data.browserInfo.screenWidth,
                timeZoneOffset: state.data.browserInfo.timeZoneOffset,
                acceptHeader: state.data.browserInfo.acceptHeader,
                javaScriptEnabled: state.data.browserInfo.javaScriptEnabled,
            },
            paymentMethod: {
                type: state.data.paymentMethod.type,
                encryptedCardNumber: state.data.paymentMethod.encryptedCardNumber,
                encryptedExpiryMonth: state.data.paymentMethod.encryptedExpiryMonth,
                encryptedExpiryYear: state.data.paymentMethod.encryptedExpiryYear,
                encryptedSecurityCode: state.data.paymentMethod.encryptedSecurityCode,
                holderName: state.data.paymentMethod.holderName,
            },
            origin: window.location.origin,
        } as AuthorisePaymentRequest

        if ((config.env !== 'prod' || testLocations.includes(executingLocationId)) && showTestOptions) {
            request.testOptions = {
                force3DSecure: force3DSecure,
            }
        }

        logAuthorisePayment(paymentReference)
        const response = await authorisePayment(paymentReference, request)

        // We don't want to call the cancelPayment endpoint when we are redirecting to the returnUrl, the failureReturnUrl or to 3D Secure.
        // Therefore, we remove the onUnload handler here.
        window.removeEventListener('unload', onUnload)
        window.removeEventListener('beforeunload', onUnload)

        // Payment was authorised or refused, so we want to redirect to the returnUrl.
        // The returnUrl in the response here will contain either the returnUrl or failureReturnUrl which was provided
        // in the request when initiating the payment, depending on whether the payment was authorised or refused.
        if (response.isPaymentResultAvailable) {
            logRedirectToReturnUrl(paymentReference)

            console.log('Redirecting to returnUrl: ', response.returnUrl)
            window.location.href = response.returnUrl!
            return
        }

        logRedirectToAction(paymentReference)

        component.handleAction(response.action)
    }

    const createCheckoutConfig = (sessionId: string, sessionData: string, force3DSecure: boolean) => {
        return {
            environment: config.adyen.env,
            clientKey: config.adyen.clientKey,
            session: {
                id: sessionId,
                sessionData,
            },
            paymentMethodsConfiguration: {
                card: {
                    showPayButton: true,

                    // This field can be used for testing different authorisation results.
                    // See all options here: https://docs.adyen.com/development-resources/testing/result-code-testing/adyen-response-codes
                    hasHolderName: showTestOptions,
                    setStatusAutomatically: false,
                },
            },
            onSubmit: (state: unknown, component: React.ReactNode) => onSubmitPayment(state, component, force3DSecure),
        }
    }

    return (
        <Container beforeNavigateBack={onBackButtonClicked}>
            <h1 className="mb-3">Pay with card</h1>

            {(config.env !== 'prod' || testLocations.includes(executingLocationId)) && (
                <div className="flex justify-end mb-3">
                    <ToggleSwitch label="Show test options" checked={showTestOptions} onChange={setShowTestOptions} />
                </div>
            )}

            {showTestOptions && (
                <div className="mb-5 max-w-xl">
                    <Banner kind="warn" stretch="parent" onClose={() => null} className="mb-3">
                        <div>
                            {
                                'The "Force 3D Secure" toggle will enforce 3D Secure for the payment, regardless of any dynamic 3D Secure settings in the Adyen Customer Area. Note that you need to use one of '
                            }
                            <a
                                href={
                                    'https://docs.adyen.com/development-resources/testing/test-card-numbers#test-3d-secure-2-authentication'
                                }
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: 'rgb(85, 26, 139)' }}
                            >
                                these card numbers
                            </a>
                            {
                                " in combination with the toggle. If you don't use one of these test card numbers, 3D Secure might not be forced correctly."
                            }
                            <br />
                            <br />
                            {
                                'The "NAME ON CARD" input can be used to test different authorisation results. E.g. by entering "DECLINED" in this field, the payment will be declined. See all accepted values '
                            }
                            <a
                                href={
                                    'https://docs.adyen.com/development-resources/testing/result-code-testing/adyen-response-codes'
                                }
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: 'rgb(85, 26, 139)' }}
                            >
                                here
                            </a>
                            .
                        </div>
                    </Banner>

                    <div className="flex justify-start mb-3">
                        <ToggleSwitch label="Force 3D Secure" checked={force3DSecure} onChange={setForce3DSecure} />
                    </div>
                </div>
            )}

            {hasRequiredParams ? (
                <div id="card-checkout-container"></div>
            ) : (
                <Banner kind="warn" stretch="parent" onClose={() => null}>
                    Session ID or session data is missing
                </Banner>
            )}
        </Container>
    )
}

export default CardCheckout
